import type { BinaryReader } from '../BinaryReader';
import type { BinaryWriter } from '../BinaryWriter';

/**
 * Mirrors GameStars.Models.NetworkModels.ProfileModel (ProfileModel.cs:32-70).
 * 16 wire elements: 8 strings each followed by its readonly bool, in this exact order:
 *   Username, UsernameReadonly, CardHolderName, CardHolderNameReadonly,
 *   Email, EmailReadonly, Mobile, MobileReadonly,
 *   Shaba, ShabaReadonly, CardNumber, CardNumberReadonly,
 *   AccountNumber, AccountNumberReadonly, BankName, BankNameReadonly.
 *
 * Same layout in both directions (GetProfile S2C response and SaveProfile C2S request).
 * On SaveProfile the server only persists the 5 bank fields (CardHolderName, Shaba,
 * CardNumber, AccountNumber, BankName) and ignores Username/Email/Mobile — but the client
 * must still serialize all 16 elements in order. The *Readonly bools are sent as false.
 */
export interface ProfileModel {
  username: string;
  usernameReadonly: boolean;
  cardHolderName: string;
  cardHolderNameReadonly: boolean;
  email: string;
  emailReadonly: boolean;
  mobile: string;
  mobileReadonly: boolean;
  shaba: string;
  shabaReadonly: boolean;
  cardNumber: string;
  cardNumberReadonly: boolean;
  accountNumber: string;
  accountNumberReadonly: boolean;
  bankName: string;
  bankNameReadonly: boolean;
}

export function decodeProfileModel(r: BinaryReader): ProfileModel {
  return {
    username: r.getString(),
    usernameReadonly: r.getBool(),
    cardHolderName: r.getString(),
    cardHolderNameReadonly: r.getBool(),
    email: r.getString(),
    emailReadonly: r.getBool(),
    mobile: r.getString(),
    mobileReadonly: r.getBool(),
    shaba: r.getString(),
    shabaReadonly: r.getBool(),
    cardNumber: r.getString(),
    cardNumberReadonly: r.getBool(),
    accountNumber: r.getString(),
    accountNumberReadonly: r.getBool(),
    bankName: r.getString(),
    bankNameReadonly: r.getBool(),
  };
}

export function writeProfileModel(w: BinaryWriter, p: ProfileModel): void {
  w.putString(p.username).putBool(p.usernameReadonly);
  w.putString(p.cardHolderName).putBool(p.cardHolderNameReadonly);
  w.putString(p.email).putBool(p.emailReadonly);
  w.putString(p.mobile).putBool(p.mobileReadonly);
  w.putString(p.shaba).putBool(p.shabaReadonly);
  w.putString(p.cardNumber).putBool(p.cardNumberReadonly);
  w.putString(p.accountNumber).putBool(p.accountNumberReadonly);
  w.putString(p.bankName).putBool(p.bankNameReadonly);
}

/** Build a SaveProfile DTO from the editable bank fields (readonly flags default false, like Unity). */
export function buildSaveProfile(fields: {
  username?: string;
  email?: string;
  mobile?: string;
  cardHolderName: string;
  shaba: string;
  cardNumber: string;
  accountNumber: string;
  bankName: string;
}): ProfileModel {
  return {
    username: fields.username ?? '',
    usernameReadonly: false,
    cardHolderName: fields.cardHolderName,
    cardHolderNameReadonly: false,
    email: fields.email ?? '',
    emailReadonly: false,
    mobile: fields.mobile ?? '',
    mobileReadonly: false,
    shaba: fields.shaba,
    shabaReadonly: false,
    cardNumber: fields.cardNumber,
    cardNumberReadonly: false,
    accountNumber: fields.accountNumber,
    accountNumberReadonly: false,
    bankName: fields.bankName,
    bankNameReadonly: false,
  };
}
